import React, { useState, useEffect, useRef } from "react";
import './Home.css';
import courseVideo from '../assets/course-video.mp4';
import meetingsBg from '../assets/meetings-bg.jpg';
import programAtGlanceImg from '../assets/background/MPSc program at a glance.jpg';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsToShow, setCardsToShow] = useState(3);
  const videoRef = useRef(null);

  // Program feature cards data
  const featureCards = [
    {
      icon: "fas fa-handshake",
      title: "Co-operative Education",
              description: "The MPSc program was among the first in Canada to offer a co-operative graduate degree in science. Students complete two semester-long work placements under faculty guidance while earning competitive salaries."
    },
    {
      icon: "fas fa-users",
      title: "Community Engagement",
      description: "Students complete pro bono projects with community partners, supporting program development or evaluation. These collaborations help organizations secure funding."
    },
    {
      icon: "fas fa-graduation-cap",
      title: "Student Success",
      description: "Over 150 students have graduated from the program. The program has a 99% success rate, and 100% have completed on time within the two-year program length."
    },
    {
      icon: "fas fa-briefcase",
      title: "Graduate Employment",
      description: "Our graduates work in government, academia, consulting, and community organizations, contributing to evidence-based policy decisions."
    },
    {
      icon: "fas fa-certificate",
      title: "Accreditation",
      description: "The program has been accredited since 2005. We are members of Co-operative Education and Work-Integrated Learning Canada (CEWIL Canada)."
    },
    {
      icon: "fas fa-trophy",
      title: "Awards & Recognition",
              description: "MPSc students regularly compete in the Canadian Evaluation Society's National Case Competition, with top-three finishes in eight of the past 18 years."
    }
  ];

  // Ensure video plays inline on iOS
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.setAttribute('webkit-playsinline', 'true');
      videoRef.current.setAttribute('x5-playsinline', 'true');
    }
  }, []);

  // Responsive carousel functionality
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 700) {
        setCardsToShow(1);
      } else if (window.innerWidth < 1100) {
        setCardsToShow(2);
      } else {
        setCardsToShow(3);
      }
      // Force re-render to recalculate card dimensions
      setCurrentSlide(prev => prev);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carousel navigation functions
  const totalSlides = Math.ceil(featureCards.length / cardsToShow);
  
  // Responsive card width and gap calculations
  const getCardWidth = () => {
    if (window.innerWidth < 700) {
      return Math.min(30, (window.innerWidth - 8) * 0.85); // Account for container padding and arrow space, use 85% of available width
    } else if (window.innerWidth < 1100) {
      return 35;
    } else {
      return 35;
    }
  };
  
  const getCardGap = () => {
    if (window.innerWidth < 700) {
      return 1.5; // Smaller gap on mobile
    } else {
      return 3;
    }
  };
  
  const cardWidth = getCardWidth();
  const cardGap = getCardGap();
  const translateX = currentSlide * cardsToShow * (cardWidth + cardGap);

  const goToSlide = (index) => {
    setCurrentSlide(Math.max(0, Math.min(index, totalSlides - 1)));
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % totalSlides);
  };

  const prevSlide = () => {
    goToSlide((currentSlide - 1 + totalSlides) % totalSlides);
  };

  // Add empty cards to fill last slide if needed
  const paddedCards = featureCards.length % cardsToShow === 0 ? featureCards : [
    ...featureCards,
    ...Array(cardsToShow - (featureCards.length % cardsToShow)).fill({ empty: true })
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section">
        <video ref={videoRef} src={courseVideo} loop autoPlay muted playsInline></video>
        <div className="hero-overlay">
          <div className="hero-container">
            <div className="hero-content">
              <div className="hero-text">
                <p className="hero-subtitle">Welcome to</p>
                <h1 className="hero-title">Masters of Applied Psychological Science</h1>
                <p className="hero-subtitle">(The MAPS Program)</p>
                <p className="hero-description">
                  Bringing Science & Social Programs Together. Our program emphasizes the application of psychological science in real-world settings, combining rigorous research methods with practical experience through co-operative education.
                </p>
                <a href="https://www.mun.ca/become/graduate/apply-to-memorial/" className="cta-button">Apply Now</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Features Section */}
      <section className="features-section">
        <div className="features-background">
          <img src={meetingsBg} alt="Background" />
        </div>
        <div className="features-container">
          <h2 className="section-title">What Sets Us Apart</h2>
          <div className="carousel-container">
            <button className="carousel-arrow prev-arrow" onClick={prevSlide}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <div 
              className="carousel-wrapper" 
              style={{ 
                width: window.innerWidth < 700 ? `${cardWidth}rem` : `calc(${cardsToShow} * ${cardWidth}rem + ${(cardsToShow - 1)} * ${cardGap}rem)`,
                maxWidth: '100%'
              }}
            >
              <div
                className="carousel-track"
                style={{
                  width: `calc(${paddedCards.length} * ${cardWidth}rem + ${(paddedCards.length - 1)} * ${cardGap}rem)`,
                  transform: `translateX(-${translateX}rem)`,
                  transition: 'transform 0.5s cubic-bezier(0.77, 0, 0.175, 1)',
                  gap: `${cardGap}rem`
                }}
              >
                {paddedCards.map((card, index) => (
                  card.empty ? (
                    <div key={index} className="feature-card empty-card" style={{ width: `${cardWidth}rem`, minWidth: `${cardWidth}rem` }} />
                  ) : (
                    <div key={index} className="feature-card" style={{ width: `${cardWidth}rem`, minWidth: `${cardWidth}rem` }}>
                      <div className="card-icon">
                        <i className={card.icon}></i>
                      </div>
                      <h3 className="card-title">{card.title}</h3>
                      <p className="card-description">{card.description}</p>
                    </div>
                  )
                ))}
              </div>
            </div>
            <button className="carousel-arrow next-arrow" onClick={nextSlide}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
          <div className="carousel-indicators">
            {Array.from({ length: totalSlides }, (_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Program Highlights Section */}
      <section className="highlights-section">
        <div className="highlights-container">
          <div className="highlights-header">
            <h2>Key Program Goals</h2>
          </div>
          <div className="highlights-right">
            <div className="highlight-box">
              <div className="goal-icon">
                <i className="fas fa-flask"></i>
              </div>
              <h2>Applied Research</h2>
              <p>Application of psychological methods and knowledge to the development and improvement of social programs and applied research.</p>
            </div>
            <div className="highlight-box">
              <div className="goal-icon">
                <i className="fas fa-brain"></i>
              </div>
              <h2>Critical Thinking</h2>
              <p>Application of critical thinking skills and evaluation technologies to evaluate social programs.</p>
            </div>
            <div className="highlight-box">
              <div className="goal-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h2>Program Evaluation</h2>
              <p>Development of program evaluation skills that are broadly applicable across all types of programs (government, industry, community, health, education and justice systems).</p>
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes Section */}
      <section className="career-outcomes">
        <div className="container">
          <div className="outcomes-text">
            <h2>Career Outcomes</h2>
            <p>
              Successful completion of the MPSc. degree qualifies students for a variety of employment positions or entrance into a doctoral program.
            </p>
          </div>
          <div className="career-areas">
            <div className="career-area">
              <h3>Government</h3>
              <ul>
                <li>Senior Policy Positions</li>
                <li>Program Evaluation</li>
                <li>Research &amp; Development</li>
              </ul>
            </div>
            <div className="career-area">
              <h3>Academia</h3>
              <ul>
                <li>Faculty Positions</li>
                <li>Research Administration</li>
                <li>Teaching &amp; Mentoring</li>
              </ul>
            </div>
            <div className="career-area">
              <h3>Private Sector</h3>
              <ul>
                <li>Consulting Firms</li>
                <li>Market Research</li>
                <li>Organizational Development</li>
              </ul>
            </div>
            <div className="career-area">
              <h3>Community Organizations</h3>
              <ul>
                <li>Program Development</li>
                <li>Non-profit Leadership</li>
                <li>Social Impact Assessment</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="facts">
        <div className="container">
          <div className="head">
            <h1>MPSc Program <br /> At a Glance</h1>
          </div>
          <div className="about">
            <div className="scores">
              <div className="right">
                <div className="percent">
                  <p>99%</p>
                  <span>Success Rate</span>
                </div>
                <div className="percent">
                  <p>100%</p>
                  <span>On-Time Completion</span>
                </div>
                <div className="percent">
                  <p>6-10</p>
                  <span>Students per Cohort</span>
                </div>
              </div>
              <div className="left">
                <div className="percent">
                  <p>150+</p>
                  <span>Graduates</span>
                </div>
                <div className="percent">
                  <p>2</p>
                  <span>Years Duration</span>
                </div>
              </div>
            </div>
            <div className="image">
              <img src={programAtGlanceImg} alt="MPSc Program" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-header">
            <h2>Get In Touch</h2>
            <p>Questions about our program? We're here to help.</p>
          </div>
          <div className="contact-info">
            <div className="contact-card">
              <div className="contact-icon">
                <i className="fas fa-user-tie"></i>
              </div>
              <h3>Program Chair</h3>
              <p>Dr. Cathryn Button</p>
              <p>Meet our program coordinator who oversees the MPSc program and student success.</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <h3>Email Us</h3>
              <p>cbutton@mun.ca</p>
              <p>psychgradofficer@mun.ca</p>
              <p>For general inquiries about the program and application process.</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <i className="fas fa-university"></i>
              </div>
              <h3>Department</h3>
              <p>Psychology, Memorial University</p>
              <p>Located in the Department of Psychology at Memorial University of Newfoundland.</p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <h3>Location</h3>
              <p>St. John's, Newfoundland</p>
              <p>Our program is based in the beautiful coastal city of St. John's, Newfoundland.</p>
            </div>
          </div>
        </div>
      </section>

        <hr className="contact-divider" />
        <div className="footer">
          <p className="copyright">COPYRIGHT © 2025 MPSc PROGRAM, MEMORIAL UNIVERSITY. ALL RIGHTS RESERVED.</p>
          <p className="program-name">Masters of Applied Psychological Science</p>
        </div>
    </>
  );
};

export default Home; 
